import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { t } from "@travily/i18n";
import { getDisplayLocale } from "@ui/lib/format-price";
import type { DateValue } from "@ui/primitives/shared";

// Date-only ISO strings must go through parseDate — `new Date("2026-05-18")`
// parses as UTC midnight and renders a day early west of UTC.
const toDate = (iso: string) => parseDate(iso).toDate(getLocalTimeZone());

const DAY_MONTH: Intl.DateTimeFormatOptions = { month: "long", day: "numeric" };
const DAY_MONTH_YEAR: Intl.DateTimeFormatOptions = { ...DAY_MONTH, year: "numeric" };

/**
 * "May 18 – May 20, 2026" — both endpoints spelled out per the design
 * (Intl.formatRange would collapse the shared month). Cross-year ranges
 * qualify both endpoints; single-day sessions show one date.
 */
export function formatSessionRange(startIso: string, endIso: string): string {
  const locale = getDisplayLocale();
  const end = new Intl.DateTimeFormat(locale, DAY_MONTH_YEAR).format(toDate(endIso));
  if (startIso === endIso) return end;

  const sameYear = parseDate(startIso).year === parseDate(endIso).year;
  const start = new Intl.DateTimeFormat(locale, sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(toDate(startIso));
  return `${start} – ${end}`;
}

/** "June 2, 2026 (Tuesday)" — check-in/out labels in the flexible picker. */
export function formatDayWithWeekday(date: DateValue): string {
  const locale = getDisplayLocale();
  const jsDate = date.toDate(getLocalTimeZone());
  return t("components.views.programs.show.availability_date_with_weekday", {
    date: new Intl.DateTimeFormat(locale, DAY_MONTH_YEAR).format(jsDate),
    weekday: new Intl.DateTimeFormat(locale, { weekday: "long" }).format(jsDate),
  });
}
